import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L, { LatLngExpression } from "leaflet";
import { Company } from "../utils/companyUtils";

interface ClusterMarkersProps {
  companies: Company[];
  showCompanies: boolean;
  onCompanyClick?: (company: Company) => void;
}

const ClusterMarkers: React.FC<ClusterMarkersProps> = ({
  companies,
  showCompanies,
  onCompanyClick,
}) => {
  const map = useMap();
  const markersRef = useRef<any[]>([]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const clearMarkers = () => {
      markersRef.current.forEach((marker) => {
        if ((map as any)?.hasLayer(marker)) {
          (map as any).removeLayer(marker);
        }
      });
      markersRef.current = [];
    };

    const DOT_SIZE = 14;
    const OVERLAP_THRESHOLD = DOT_SIZE / 2; // Chỉ gộp khi chồng lên >50%

    const renderMarkers = () => {
      clearMarkers();

      if (!showCompanies || !companies || companies.length === 0) {
        return;
      }

      const clusters: {
        latlng: LatLngExpression | any;
        point: any;
        companies: Company[];
      }[] = [];

      companies.forEach((company) => {
        if (!company.coordinates || company.coordinates.length < 2) return;

        const latlng = (L as any).latLng(
          company.coordinates[1],
          company.coordinates[0]
        );
        const point = (map as any).latLngToLayerPoint(latlng);

        let targetCluster = clusters.find((cluster) => {
          return cluster.point.distanceTo(point) <= OVERLAP_THRESHOLD;
        });

        if (targetCluster) {
          const currentCount = targetCluster.companies.length;
          targetCluster.companies.push(company);
          targetCluster.latlng = (L as any).latLng(
            (targetCluster.latlng.lat * currentCount + latlng.lat) /
              (currentCount + 1),
            (targetCluster.latlng.lng * currentCount + latlng.lng) /
              (currentCount + 1)
          );
          targetCluster.point = targetCluster.point
            .multiplyBy(currentCount)
            .add(point)
            .divideBy(currentCount + 1);
        } else {
          clusters.push({
            latlng,
            point,
            companies: [company],
          });
        }
      });

      clusters.forEach((cluster) => {
        if (cluster.companies.length === 1) {
          const company = cluster.companies[0];
          const marker = (L as any).marker(
            [company.coordinates[1], company.coordinates[0]],
            {
              icon: (L as any).divIcon({
                className: "company-marker",
                html: `<div style="
                background-color: white;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                border: 2px solid #808080;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              "></div>`,
                iconSize: [14, 14],
                iconAnchor: [7, 7],
              }),
            }
          );

        marker.bindPopup(`
            <div>
              <h4>${company.name}</h4>
              <p><strong>Address:</strong> ${company.address}</p>
              <p><strong>Region:</strong> ${company.region || "Unknown"}</p>
            ${
              company.topics
                ? `<p><strong>Topics:</strong> ${company.topics}</p>`
                : ""
            }
            ${
              company.membership
                ? `<p><strong>Membership:</strong> ${company.membership}</p>`
                : ""
            }
            ${
              company.displayed
                ? `<p><strong>Displayed:</strong> ${company.displayed}</p>`
                : ""
            }
            </div>
          `);

          marker.on("click", () => {
            if (onCompanyClick) {
              onCompanyClick(company);
            }
          });

          marker.addTo(map);
          markersRef.current.push(marker);
        } else {
          const count = cluster.companies.length;
          const size = Math.min(52, 24 + count * 1.2);

          const overlayMarker = (L as any).marker(cluster.latlng, {
            zIndexOffset: 900,
            icon: (L as any).divIcon({
              className: "company-cluster-overlay",
              html: `<div style="
                  background-color: white;
                  width: ${size}px;
                  height: ${size}px;
                  border-radius: 50%;
                  border: 3px solid #34A936;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.35);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-weight: bold;
                  font-size: 12px;
                  color: #34A936;
                ">${count}</div>`,
              iconSize: [size, size],
              iconAnchor: [size / 2, size / 2],
            }),
          });

          overlayMarker.bindPopup(`
            <div>
              <h4>${count} companies at this location</h4>
              <div style="max-height: 250px; overflow-y: auto; padding-right: 8px;">
                ${cluster.companies
                  .map(
                    (company) => `
                  <div style="margin: 8px 0; padding: 4px; border-left: 3px solid #34A936;">
                    <strong>${company.name}</strong><br>
                    <small>${company.address}</small><br>
                    <small>Region: ${company.region || "Unknown"}</small><br>
                    ${
                      company.topics
                        ? `<small>Topics: ${company.topics}</small><br>`
                        : ""
                    }
                    ${
                      company.membership
                        ? `<small>Membership: ${company.membership}</small><br>`
                        : ""
                    }
                    ${
                      company.displayed
                        ? `<small>Displayed: ${company.displayed}</small>`
                        : ""
                    }
                  </div>
                `
                  )
                  .join("")}
              </div>
            </div>
          `);

          overlayMarker.on("click", () => {
            if (count === 1 && onCompanyClick) {
              onCompanyClick(cluster.companies[0]);
            }
          });

          overlayMarker.addTo(map);
          markersRef.current.push(overlayMarker);
        }
      });
    };

    renderMarkers();
    (map as any).on("zoomend", renderMarkers);

    return () => {
      (map as any).off("zoomend", renderMarkers);
      clearMarkers();
    };
  }, [map, companies, showCompanies, onCompanyClick]);

  return null;
};

export default ClusterMarkers;
